import { useCallback, useEffect, useState } from 'react'
import { useDataProvider } from 'react-admin'
import moment from 'moment'
import formatRefInvoice from '../utils/formatRefInvoice'

interface State {
  vendorOrders?: number
  countryOrders?: number
  customerOrders?: number
  ref_invoice?: string
}

interface Props {
  vendor_fk_code?: string
  country?: string
  customer_fk_code?: string
  pi_date?: string
}

export default function useGetNewRefInvoice(props: Props) {
  const [state, setState] = useState<State>({})

  const { vendor_fk_code, country, customer_fk_code, pi_date } = props
  const dataProvider = useDataProvider()

  const year = pi_date ? moment(pi_date).year() : new Date().getFullYear()

  const fetchVendorInvoices = useCallback(async () => {
    const { total: vendorCount } = await dataProvider.getList('invoice_header', {
      filter: {
        'order#vendor_fk_code@_eq': vendor_fk_code,
        'order#pi_date@_gte': `${year}-01-01`,
        'order#pi_date@_lte': `${year}-12-31`,
      },
      sort: { field: 'date', order: 'asc' },
      pagination: { page: 1, perPage: 1 },
    })

    setState((state) => ({
      ...state,
      vendorOrders: vendor_fk_code ? vendorCount + 1 : 0,
    }))
  }, [dataProvider, vendor_fk_code, year])

  const fetchCountryInvoices = useCallback(async () => {
    const { total: countryCount } = await dataProvider.getList('invoice_header', {
      filter: {
        'order#vendor#country@_eq': country,
        'order#pi_date@_gte': `${year}-01-01`,
        'order#pi_date@_lte': `${year}-12-31`,
      },
      sort: { field: 'date', order: 'asc' },
      pagination: { page: 1, perPage: 1 },
    })

    setState((state) => ({
      ...state,
      countryOrders: country ? countryCount + 1 : 0,
    }))
  }, [dataProvider, country, year])

  const fetchCustomerInvoices = useCallback(async () => {
    let customerOrders = 0
    const { total: customerCount } = await dataProvider.getList('invoice_header', {
      filter: {
        'order#customer_fk_code@_eq': customer_fk_code,
        'order#pi_date@_gte': `${year}-01-01`,
        'order#pi_date@_lte': `${year}-12-31`,
      },

      sort: { field: 'date', order: 'asc' },
      pagination: { page: 1, perPage: 1 },
    })

    customerOrders = customer_fk_code ? customerCount + 1 : 0

    setState((state) => ({
      ...state,
      customerOrders,
    }))
  }, [dataProvider, customer_fk_code, year])

  useEffect(() => {
    fetchVendorInvoices()
    fetchCountryInvoices()
    fetchCustomerInvoices()

    return () => {
      setState({})
    }
  }, [fetchCustomerInvoices, fetchCountryInvoices, fetchVendorInvoices])

  useEffect(() => {
    if ((vendor_fk_code || customer_fk_code) && pi_date) {
      const ref_invoice = formatRefInvoice({
        vendor_fk_code: vendor_fk_code || '',
        customer_fk_code: customer_fk_code || '',
        vendorCount: state.vendorOrders || 0,
        countryCount: state.countryOrders || 0,
        customerCount: state.customerOrders || 0,
        pi_date,
      })
      setState((state) => ({
        ...state,
        ref_invoice,
      }))
    }
  }, [state.vendorOrders, state.countryOrders, state.customerOrders, vendor_fk_code, customer_fk_code, pi_date])

  return state
}
